import React, { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { motion } from "framer-motion";
import {
  FiArrowLeft,
  FiBookmark,
  FiExternalLink,
  FiFileText,
  FiUsers,
  FiCalendar,
  FiHome,
  FiHash,
  FiCopy,
  FiCheck,
  FiAlertCircle,
  FiSearch,
  FiUnlock,
  FiBook,
  FiTag,
  FiList,
  FiGrid,
  FiLink,
  FiTrendingUp,
} from "react-icons/fi";
import {
  getPaperByDoi,
  savePaper,
  unsavePaper,
  checkIfPaperIsSaved,
  trackPaperView,
  getRelatedPapers,
} from "../../services/paperService";
import { useUserContext } from "../../context/UserContext";
import "./styles/PaperDetail.css";

const PaperDetail = () => {
  const { doi } = useParams();
  const { user, isAuthenticated } = useUserContext();
  const [paper, setPaper] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState("");
  const [isSaved, setIsSaved] = useState(false);
  const [saveLoading, setSaveLoading] = useState(false);
  const [saveError, setSaveError] = useState("");
  const [copySuccess, setCopySuccess] = useState(false);

  // NEW: Related papers state
  const [relatedPapers, setRelatedPapers] = useState([]);
  const [relatedPapersLoading, setRelatedPapersLoading] = useState(false);

  useEffect(() => {
    const fetchPaper = async () => {
      try {
        setIsLoading(true);
        const response = await getPaperByDoi(doi);

        if (response && response.data) {
          setPaper(response.data);
          trackPaperView(doi);

          if (isAuthenticated && user) {
            try {
              const saved = await checkIfPaperIsSaved(doi);
              setIsSaved(saved);
            } catch (err) {
              // Silently fail
            }
          }
        } else {
          throw new Error("Invalid response format");
        }
      } catch (err) {
        setError("Failed to load paper details. Please try again.");
      } finally {
        setIsLoading(false);
      }
    };

    if (doi) {
      fetchPaper();
    }
  }, [doi, isAuthenticated, user]);

  // NEW: Fetch related papers
  useEffect(() => {
    const fetchRelatedPapers = async () => {
      if (!doi) {
        return;
      }

      try {
        setRelatedPapersLoading(true);
        const response = await getRelatedPapers(doi);

        if (response && response.relatedPapers) {
          setRelatedPapers(response.relatedPapers);
        }
      } catch (err) {
        // Silently handle related papers fetch errors
      } finally {
        setRelatedPapersLoading(false);
      }
    };

    // Fetch related papers after main paper is loaded
    if (paper && doi) {
      fetchRelatedPapers();
    }
  }, [paper, doi]);

  const handleSaveToggle = async () => {
    if (!isAuthenticated) {
      setSaveError("Please log in to save papers");
      return;
    }

    setSaveLoading(true);
    setSaveError("");

    try {
      if (isSaved) {
        await unsavePaper(doi);
        setIsSaved(false);
      } else {
        await savePaper(doi);
        setIsSaved(true);
      }
    } catch (err) {
      setSaveError("Failed to update saved status");
    } finally {
      setSaveLoading(false);
    }
  };

  const handleCopyCitation = async () => {
    if (!paper?.citationApa) return;

    try {
      await navigator.clipboard.writeText(paper.citationApa);
      setCopySuccess(true);
      setTimeout(() => setCopySuccess(false), 3000);
    } catch (err) {
      const textArea = document.createElement("textarea");
      textArea.value = paper.citationApa;
      document.body.appendChild(textArea);
      textArea.focus();
      textArea.select();

      try {
        document.execCommand("copy");
        setCopySuccess(true);
        setTimeout(() => setCopySuccess(false), 3000);
      } catch (err) {
        // Silently fail
      }

      document.body.removeChild(textArea);
    }
  };

  const validateUrl = (url) => {
    if (!url) return "";
    if (!/^https?:\/\//i.test(url)) return "";
    return url;
  };

  // Loading state
  if (isLoading) {
    return (
      <div className="exp-detail-container">
        <div className="exp-detail-wrapper">
          <motion.div
            className="exp-detail-loading"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
          >
            <div className="exp-detail-spinner" />
            <p>Loading paper details...</p>
          </motion.div>
        </div>
      </div>
    );
  }

  // Error state
  if (error) {
    return (
      <div className="exp-detail-container">
        <div className="exp-detail-wrapper">
          <motion.div
            className="exp-detail-error"
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
          >
            <div className="exp-detail-error-icon">
              <FiAlertCircle />
            </div>
            <p className="exp-detail-error-text">{error}</p>
            <Link to="/explore" className="exp-detail-back">
              <FiArrowLeft /> Back to search
            </Link>
          </motion.div>
        </div>
      </div>
    );
  }

  // Not found state
  if (!paper) {
    return (
      <div className="exp-detail-container">
        <div className="exp-detail-wrapper">
          <motion.div
            className="exp-detail-not-found"
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
          >
            <div className="exp-detail-not-found-icon">
              <FiSearch />
            </div>
            <h2 className="exp-detail-not-found-title">Paper Not Found</h2>
            <p className="exp-detail-not-found-text">
              The requested paper could not be found.
            </p>
            <Link to="/explore" className="exp-detail-back">
              <FiArrowLeft /> Back to search
            </Link>
          </motion.div>
        </div>
      </div>
    );
  }

  // Extract unique domains and fields
  const domains = paper.topicCategories
    ? [...new Set(paper.topicCategories.map((topic) => topic.domain))]
    : [];

  const fields = paper.topicCategories
    ? [...new Set(paper.topicCategories.map((topic) => topic.field))]
    : [];

  return (
    <div className="exp-detail-container">
      <div className="exp-detail-wrapper">
        {/* Back Navigation */}
        <motion.div
          initial={{ opacity: 0, x: -20 }}
          animate={{ opacity: 1, x: 0 }}
        >
          <Link to="/explore" className="exp-detail-back">
            <FiArrowLeft /> Back to search
          </Link>
        </motion.div>

        {/* Main Card */}
        <motion.div
          className="exp-detail-card"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1 }}
        >
          {/* Header */}
          <div className="exp-detail-header">
            <h1 className="exp-detail-title">{paper.title}</h1>

            <div className="exp-detail-badges">
              {paper.openAccess && (
                <span className="exp-badge exp-badge--open-access">
                  <FiUnlock /> Open Access
                </span>
              )}
            </div>
          </div>

          {/* Meta Information */}
          <div className="exp-detail-meta">
            <div className="exp-detail-meta-row">
              <div className="exp-detail-meta-icon">
                <FiUsers />
              </div>
              <div className="exp-detail-meta-content">
                <span className="exp-detail-meta-label">Authors</span>
                <span className="exp-detail-meta-value">
                  {paper.authors && paper.authors.length > 0
                    ? paper.authors.join(", ")
                    : "Unknown"}
                </span>
              </div>
            </div>

            <div className="exp-detail-meta-row">
              <div className="exp-detail-meta-icon">
                <FiCalendar />
              </div>
              <div className="exp-detail-meta-content">
                <span className="exp-detail-meta-label">Published</span>
                <span className="exp-detail-meta-value">
                  {paper.publicationYear || "N/A"}
                  {paper.journal && ` in ${paper.journal}`}
                </span>
              </div>
            </div>

            {paper.publisher && (
              <div className="exp-detail-meta-row">
                <div className="exp-detail-meta-icon">
                  <FiHome />
                </div>
                <div className="exp-detail-meta-content">
                  <span className="exp-detail-meta-label">Publisher</span>
                  <span className="exp-detail-meta-value">
                    {paper.publisher}
                  </span>
                </div>
              </div>
            )}

            <div className="exp-detail-meta-row">
              <div className="exp-detail-meta-icon">
                <FiHash />
              </div>
              <div className="exp-detail-meta-content">
                <span className="exp-detail-meta-label">DOI</span>
                <span className="exp-detail-meta-value">
                  {paper.doi ? (
                    <a
                      href={`https://doi.org/${encodeURIComponent(paper.doi)}`}
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      {paper.doi}
                    </a>
                  ) : (
                    "N/A"
                  )}
                </span>
              </div>
            </div>

            {paper.citationCount !== undefined && (
              <div className="exp-detail-meta-row">
                <div className="exp-detail-meta-icon">
                  <FiBook />
                </div>
                <div className="exp-detail-meta-content">
                  <span className="exp-detail-meta-label">Citations</span>
                  <span className="exp-detail-meta-value">
                    {paper.citationCount}
                  </span>
                </div>
              </div>
            )}
          </div>

          {/* Actions Bar */}
          <div className="exp-detail-actions">
            {paper.openAccess && paper.pdfUrl && validateUrl(paper.pdfUrl) && (
              <a
                href={validateUrl(paper.pdfUrl)}
                target="_blank"
                rel="noopener noreferrer"
                className="exp-detail-action-btn exp-detail-action-btn--pdf"
              >
                <FiFileText /> Download PDF
              </a>
            )}

            {paper.url && validateUrl(paper.url) && (
              <a
                href={validateUrl(paper.url)}
                target="_blank"
                rel="noopener noreferrer"
                className="exp-detail-action-btn exp-detail-action-btn--source"
              >
                <FiExternalLink /> View Source
              </a>
            )}

            <motion.button
              onClick={handleSaveToggle}
              disabled={saveLoading}
              className={`exp-detail-action-btn exp-detail-action-btn--save ${
                isSaved ? "saved" : ""
              }`}
              aria-label={isSaved ? "Unsave Paper" : "Save Paper"}
              whileTap={{ scale: 0.95 }}
            >
              <FiBookmark />
              {isSaved ? "Saved" : "Save Paper"}
            </motion.button>

            {saveError && (
              <div className="exp-detail-save-error">{saveError}</div>
            )}
          </div>

          {/* Abstract Section */}
          <div className="exp-detail-section">
            <h3 className="exp-detail-section-title">
              <FiList /> Abstract
            </h3>
            <p className="exp-detail-abstract">
              {paper.abstract || "No abstract available."}
            </p>
          </div>

          {/* Research Categories Section */}
          {(domains.length > 0 ||
            fields.length > 0 ||
            (paper.topics && paper.topics.length > 0)) && (
            <div className="exp-detail-section">
              <h3 className="exp-detail-section-title">
                <FiGrid /> Research Classification
              </h3>
              <div className="exp-detail-categories">
                {domains.length > 0 && (
                  <div className="exp-detail-category-group">
                    <span className="exp-detail-category-label">Domains</span>
                    <div className="exp-detail-category-badges">
                      {domains.map((domain, index) => (
                        <span
                          key={`domain-${index}`}
                          className="exp-detail-category-badge exp-detail-category-badge--domain"
                        >
                          {domain}
                        </span>
                      ))}
                    </div>
                  </div>
                )}

                {fields.length > 0 && (
                  <div className="exp-detail-category-group">
                    <span className="exp-detail-category-label">Fields</span>
                    <div className="exp-detail-category-badges">
                      {fields.map((field, index) => (
                        <span
                          key={`field-${index}`}
                          className="exp-detail-category-badge exp-detail-category-badge--field"
                        >
                          {field}
                        </span>
                      ))}
                    </div>
                  </div>
                )}

                {paper.topics && paper.topics.length > 0 && (
                  <div className="exp-detail-category-group">
                    <span className="exp-detail-category-label">Topics</span>
                    <div className="exp-detail-category-badges">
                      {paper.topics.map((topic, index) => (
                        <span
                          key={index}
                          className="exp-detail-category-badge exp-detail-category-badge--topic"
                        >
                          {topic}
                        </span>
                      ))}
                    </div>
                  </div>
                )}

                {/* Detailed Categories Table */}
                {paper.topicCategories && paper.topicCategories.length > 0 && (
                  <table className="exp-detail-categories-table">
                    <thead>
                      <tr>
                        <th>Topic</th>
                        <th>Domain</th>
                        <th>Field</th>
                        <th>Subfield</th>
                      </tr>
                    </thead>
                    <tbody>
                      {paper.topicCategories.map((category, index) => (
                        <tr key={`category-${index}`}>
                          <td>{category.name}</td>
                          <td>{category.domain}</td>
                          <td>{category.field}</td>
                          <td>{category.subfield}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                )}
              </div>
            </div>
          )}

          {/* Citation Section */}
          {paper.citationApa && (
            <div className="exp-detail-section">
              <h3 className="exp-detail-section-title">
                <FiTag /> Citation
              </h3>
              <div className="exp-detail-citation">{paper.citationApa}</div>
              <button
                onClick={handleCopyCitation}
                className={`exp-detail-copy-btn ${copySuccess ? "copied" : ""}`}
                aria-label="Copy citation to clipboard"
              >
                {copySuccess ? (
                  <>
                    <FiCheck /> Copied!
                  </>
                ) : (
                  <>
                    <FiCopy /> Copy Citation
                  </>
                )}
              </button>
            </div>
          )}

          {/* References Section */}
          {paper.references && paper.references.length > 0 && (
            <div className="exp-detail-section">
              <h3 className="exp-detail-section-title">
                <FiBook /> References
              </h3>
              <div className="exp-detail-references">
                <ul className="exp-detail-references-list">
                  {paper.references.slice(0, 10).map((ref, index) => (
                    <li key={index} className="exp-detail-reference-item">
                      <span>
                        {ref.title || ref.text || `Reference ${index + 1}`}
                      </span>
                      {ref.doi && (
                        <Link
                          to={`/explore/${encodeURIComponent(ref.doi)}`}
                          className="exp-detail-reference-link"
                        >
                          View Details
                        </Link>
                      )}
                    </li>
                  ))}
                  {paper.references.length > 10 && (
                    <li className="exp-detail-more-refs">
                      + {paper.references.length - 10} more references
                    </li>
                  )}
                </ul>
              </div>
            </div>
          )}

          {/* NEW: Related Papers Section */}
          {(relatedPapers.length > 0 || relatedPapersLoading) && (
            <motion.div
              className="exp-detail-section exp-detail-related"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.3 }}
            >
              <h3 className="exp-detail-section-title">
                <FiLink /> Related Papers
              </h3>

              {relatedPapersLoading ? (
                <div className="exp-detail-related-loading">
                  <div className="exp-detail-spinner" />
                  <span>Finding related papers...</span>
                </div>
              ) : (
                <div className="exp-detail-related-grid">
                  {relatedPapers.slice(0, 6).map((relatedPaper, index) => (
                    <motion.div
                      key={relatedPaper.doi || relatedPaper._id || index}
                      className="exp-detail-related-card"
                      initial={{ opacity: 0, y: 10 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ delay: index * 0.1 }}
                      whileHover={{ scale: 1.02 }}
                    >
                      <Link
                        to={
                          relatedPaper.doi
                            ? `/explore/${encodeURIComponent(relatedPaper.doi)}`
                            : `/explore/${encodeURIComponent(
                                relatedPaper._id || ""
                              )}`
                        }
                        className="exp-detail-related-link"
                      >
                        <h4 className="exp-detail-related-title">
                          {relatedPaper.title}
                        </h4>

                        <div className="exp-detail-related-meta">
                          {relatedPaper.similarity && (
                            <span className="exp-detail-related-similarity">
                              <FiTrendingUp />
                              {relatedPaper.similarity}% match
                            </span>
                          )}
                          {relatedPaper.citationCount !== undefined && (
                            <span className="exp-detail-related-citations">
                              <FiBook />
                              {relatedPaper.citationCount.toLocaleString()}{" "}
                              citations
                            </span>
                          )}
                          {relatedPaper.publicationYear && (
                            <span className="exp-detail-related-year">
                              <FiCalendar />
                              {relatedPaper.publicationYear}
                            </span>
                          )}
                        </div>

                        {relatedPaper.authors &&
                          relatedPaper.authors.length > 0 && (
                            <p className="exp-detail-related-authors">
                              <FiUsers />
                              {relatedPaper.authors.slice(0, 2).join(", ")}
                              {relatedPaper.authors.length > 2 && " et al."}
                            </p>
                          )}
                      </Link>
                    </motion.div>
                  ))}
                </div>
              )}
            </motion.div>
          )}
        </motion.div>
      </div>
    </div>
  );
};

export default PaperDetail;
